/*
 * HELP-15991 Build Your Own Bundle (BYOB) progress indicator.
 *
 * Counts items in the cart toward a bundle size (default 5) and renders a
 * playful "X / N" progress bar. When the shopper passes the limit the bar
 * resets so the next bundle starts again at 1 (e.g. 6 items shows 1/5,
 * 10 items shows a full 5/5 then 11 shows 1/5).
 *
 * This is intentionally separate from Dawn's native snippets/progress-bar.liquid
 * (the bulk-quantity bar used by quick-add-bulk.js).
 *
 * Config via data attributes on the <byob-bundle-progress> element:
 *   data-bundle-size       bundle limit, default 5
 *   data-count-mode        "quantity" (sum of item quantities, default) or
 *                          "lines" (distinct line items)
 *   data-collection-handle optional; reserved for future per-collection filtering
 */
class ByobBundleProgress extends HTMLElement {
  constructor() {
    super();
    this.bundleSize = Math.max(1, parseInt(this.dataset.bundleSize, 10) || 5);
    this.countMode = this.dataset.countMode === 'lines' ? 'lines' : 'quantity';
    this.barEl = this.querySelector('[data-byob-bar]');
    this.countEl = this.querySelector('[data-byob-count]');
    this.totalEl = this.querySelector('[data-byob-total]');
    this.messageEl = this.querySelector('[data-byob-message]');
    this.srEl = this.querySelector('[data-byob-sr]');
    this.count = 0;
    this.onCartUpdate = this.onCartUpdate.bind(this);
  }

  connectedCallback() {
    if (this.totalEl) this.totalEl.textContent = this.bundleSize;
    this.buildSegments();
    this.refresh();
    this.placeBar();

    // Dawn-driven cart changes (cart drawer, native add-to-cart).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    }
    // Boost (and any other) cart changes that bypass Dawn's pubsub.
    ByobBundleProgress.installCartFetchHook();
    document.addEventListener('byob:cart-changed', this.onCartUpdate);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    document.removeEventListener('byob:cart-changed', this.onCartUpdate);
    if (this._boostWaitObserver) this._boostWaitObserver.disconnect();
    if (this._boostKeepObserver) this._boostKeepObserver.disconnect();
  }

  /*
   * Two placements:
   *  - Sticky (default): the bar floats fixed at the bottom of the viewport.
   *    Moving the wrapper onto <body> keeps it clear of any Boost ancestor with
   *    overflow/transform that would otherwise clip or re-anchor a fixed element.
   *  - Not sticky: the bar sits inline below the collection title via Boost layout.
   */
  placeBar() {
    const wrapper = this.closest('.byob-progress-section') || this;
    if (wrapper.classList.contains('byob-progress-section--sticky')) {
      if (wrapper.parentElement !== document.body) document.body.appendChild(wrapper);
    } else {
      this.placeIntoBoostLayout();
    }
  }

  /*
   * Boost renders the breadcrumb, title, toolbar and product grid as one block,
   * so this section can only be placed above or below that whole block via the
   * theme editor. To sit it in the gap *below the title and above the grid*, we
   * relocate the section wrapper to just before Boost's layout container
   * (.boost-sd-layout = sidebar + grid). Re-asserts if Boost re-renders the grid
   * on filter/sort. No-ops gracefully when Boost isn't on the page.
   */
  placeIntoBoostLayout() {
    const wrapper = this.closest('.byob-progress-section') || this;

    const place = () => {
      const layout = document.querySelector('.boost-sd-layout');
      if (!layout || !layout.parentElement) return false;
      // Only move when not already the element directly before the layout.
      if (layout.previousElementSibling !== wrapper) {
        layout.parentElement.insertBefore(wrapper, layout);
      }
      return true;
    };

    if (place()) {
      this.keepInBoostLayout(place);
      return;
    }

    // Boost not in the DOM yet — wait for it, then place and keep watching.
    this._boostWaitObserver = new MutationObserver(() => {
      if (place()) {
        this._boostWaitObserver.disconnect();
        this._boostWaitObserver = null;
        this.keepInBoostLayout(place);
      }
    });
    this._boostWaitObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  // Watch Boost's block; if a re-render drops or moves us, slot back in.
  keepInBoostLayout(place) {
    const root = document.querySelector('.boost-sd__filter-product-list') || document.body;
    this._boostKeepObserver = new MutationObserver(() => place());
    this._boostKeepObserver.observe(root, { childList: true, subtree: true });
  }

  onCartUpdate() {
    this.refresh();
  }

  buildSegments() {
    if (!this.barEl) return;
    this.barEl.innerHTML = '';
    this.segments = [];
    for (let i = 0; i < this.bundleSize; i += 1) {
      const seg = document.createElement('span');
      seg.className = 'byob-progress__segment';
      this.barEl.appendChild(seg);
      this.segments.push(seg);
    }
  }

  async refresh() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();

      const handle = this.dataset.collectionHandle;
      let count;
      if (handle) {
        const ids = await this.getCollectionProductIds(handle);
        const filtered = cart.items.filter(item => ids.has(item.product_id));
        count = this.countMode === 'lines'
          ? filtered.length
          : filtered.reduce((sum, item) => sum + item.quantity, 0);
      } else {
        count = this.countMode === 'lines' ? cart.items.length : cart.item_count;
      }
      this.update(count);
    } catch (e) {
      // Network hiccup: leave the last known state in place.
    }
  }

  // products.json caps each page at 250, but this collection holds more than
  // that, so page through until a short page signals the end. Fetching only
  // the first page left items 251+ out of the set, so adding them never moved
  // the counter.
  async getCollectionProductIds(handle) {
    if (this._collectionProductIds) return this._collectionProductIds;
    const ids = new Set();
    try {
      const root = window.Shopify?.routes?.root || '/';
      const limit = 250;
      for (let page = 1; ; page += 1) {
        const res = await fetch(`${root}collections/${handle}/products.json?limit=${limit}&page=${page}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) break;
        const data = await res.json();
        const products = data.products || [];
        products.forEach(p => ids.add(p.id));
        if (products.length < limit) break;
      }
    } catch (e) {
      // Network hiccup: cache whatever pages we managed to read.
    }
    this._collectionProductIds = ids;
    return this._collectionProductIds;
  }

  update(count) {
    const previous = this.count;
    this.count = count;

    const completedBundles = Math.floor(count / this.bundleSize);
    const remainder = count % this.bundleSize;
    // Show a full bar at exact multiples, otherwise the in-progress remainder.
    const filled = remainder === 0 && count > 0 ? this.bundleSize : remainder;
    const isComplete = filled === this.bundleSize;

    // Mobile renders a circular ring instead of the segmented bar; expose the
    // fill ratio (0–1) so the conic-gradient in CSS knows how far to sweep.
    this.style.setProperty('--byob-fill', this.bundleSize ? filled / this.bundleSize : 0);

    if (this.segments) {
      this.segments.forEach((seg, i) => {
        seg.classList.toggle('is-filled', i < filled);
      });
    }
    if (this.countEl) this.countEl.textContent = filled;
    if (this.barEl) this.barEl.setAttribute('aria-valuenow', filled);
    if (this.srEl) this.srEl.textContent = `${filled} of ${this.bundleSize} selected`;
    this.classList.toggle('is-complete', isComplete);
    this.classList.toggle('byob-progress--empty', count === 0);

    if (this.messageEl) {
      const remaining = this.bundleSize - filled;
      if (count === 0) {
        this.messageEl.textContent = `Add ${this.bundleSize} to build your bundle`;
      } else if (isComplete) {
        this.messageEl.textContent =
          completedBundles > 1
            ? `${completedBundles} bundles unlocked! Keep going for more.`
            : 'Bundle unlocked! Keep going to build another.';
      } else {
        this.messageEl.textContent = `${remaining} more to unlock your bundle`;
      }
    }

    // Pop animation only when the count actually moves up.
    if (count > previous) {
      this.classList.remove('byob-progress--bump');
      if (this.countEl) this.countEl.classList.remove('byob-progress__count-now--pop');
      // Force reflow so the animation can replay.
      void this.offsetWidth;
      this.classList.add('byob-progress--bump');
      if (this.countEl) this.countEl.classList.add('byob-progress__count-now--pop');
    }
  }

  // Patch fetch once so cart mutations from any source refresh every instance.
  static installCartFetchHook() {
    if (ByobBundleProgress._hooked) return;
    ByobBundleProgress._hooked = true;
    const nativeFetch = window.fetch;
    const cartWriteRe = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const promise = nativeFetch.apply(this, args);
      if (cartWriteRe.test(url)) {
        promise
          .then(() => {
            document.dispatchEvent(new CustomEvent('byob:cart-changed'));
          })
          .catch(() => {});
      }
      return promise;
    };
  }
}

if (!customElements.get('byob-bundle-progress')) {
  customElements.define('byob-bundle-progress', ByobBundleProgress);
}
